import {
  TOKEN_KEY,
  getAuthorization,
  setAuthorization,
  removeAuthorization,
  setAuthorization2FA,
  removeAuthorization2FA,
  setAuthorizationPasswordChange,
  removeAuthorizationPasswordChange,
  setReEnterPasswordToken,
} from 'utils/authCookie';
import { get, post, put } from 'utils/apiClient';
import { isUserWithout2FA } from 'utils/userUtils';

export async function saveIdentificationImage(data) {
  return post('auth/register_identification_image', {
    body: data,
  });
}

export async function login({ email, password }) {
  const response = await post('auth/login', { body: { email, password } });
  if (response.status < 400) {
    const { user, token } = response.data;
    if (user?.requiresPasswordChange) {
      setAuthorizationPasswordChange(token);
    } else if (!user?.requiresRoleSelection) {
      isUserWithout2FA(user)
        ? setAuthorization(token)
        : setAuthorization2FA(token);
    }
  }
  return response;
}

export async function setNewPassword({ password }) {
  const response = await post('auth/set-new-password', {
    body: { newPassword: password },
    passwordChange: true,
  });
  if (response.status < 400) {
    const { user, token } = response.data;
    removeAuthorizationPasswordChange();
    if (!user?.requiresRoleSelection) {
      isUserWithout2FA(user)
        ? setAuthorization(token)
        : setAuthorization2FA(token);
    }
  }
  return response;
}

export async function selectRole(body) {
  const response = await post('auth/select-role', { body: body });
  if (response.status < 400) {
    const { user, token } = response.data;
    isUserWithout2FA(user)
      ? setAuthorization(token)
      : setAuthorization2FA(token);
  }
  return response;
}

export async function login2faSendCode(body) {
  return await post('auth/send-code', { body: body, login2fa: true });
}

export async function login2fa(body) {
  const response = await post('auth/login2fa', { body: body, login2fa: true });
  if (response.status < 400) {
    setAuthorization(response.data.token.token);
    removeAuthorization2FA();
  }
  return response;
}

export async function loginVerifier(body) {
  const response = await post('auth/login-verifier', { body: body });
  if (response.status < 400) {
    removeAuthorization();
    setAuthorization(response.data.token.token);
  }
  return response;
}

export async function getSecurityQuestion(body) {
  return await get('auth/security-question', {
    body: body,
    login2fa: !Boolean(body.logedIn),
  });
}

export async function pauseUser(form) {
  return await post('auth/pause-user', {
    body: form,
  });
}

export async function checkPin(form) {
  const response = await post('auth/check-pin', {
    body: form,
  });
  if (response.status < 400) {
    setReEnterPasswordToken(response.data.token);
  }
  return response;
}

export async function signup({
  email,
  password,
  firstName,
  identificationImage,
  lastName,
  phone,
  pinCode,
  identification,
  selfIdentificationAnswers,
}) {
  const response = await post('auth/signup', {
    body: {
      email,
      password,
      firstName,
      lastName,
      phone,
      pinCode,
      identification,
      selfIdentificationAnswers,
    },
  });

  const { data } = response;
  const { user } = data;

  //add identification Image
  const formData = new FormData();
  formData.append('id', user._id);
  formData.append('identificationImage', identificationImage);
  formData.append('enterprise', 'Hold Access');
  formData.append('userSignup', true);
  await saveIdentificationImage(formData);

  return response;
}

export async function idkitSignup({ email }) {
  const response = await post('idkit/initiate-verification', {
    body: {
      email,
    },
  });
  return response;
}

export async function signupEnterprice(form) {
  const response = await post('auth/signup-enterprise', {
    body: form,
  });
  return response;
}

export async function signupThirdparty(form) {
  const response = await post('auth/signup-thirdparty', {
    body: form,
  });
  return response;
}

export async function logout() {
  removeAuthorization();
  removeAuthorization2FA();
  return { status: 'success' };
}

export async function getUser() {
  const token = getAuthorization();
  if (!token) {
    return null;
  }
  const response = await get('user');

  if (!response || response?.status >= 400) {
    removeAuthorization(TOKEN_KEY);
    removeAuthorization2FA();
    return null;
  }
  const {
    data: { user },
  } = response;
  return user;
}

// Unlike getUser, a failed request leaves the session intact.
export async function fetchCurrentUser() {
  const response = await get('user');
  return response?.status < 400 ? response.data.user : null;
}

export async function getVerifierUser() {
  const token = getAuthorization();
  if (!token) {
    return null;
  }
  const response = await get('user/verifier');
  if (response.status < 400) {
    const {
      data: { user },
    } = response;
    return user;
  }
  return null;
}

export async function forgotPassword(email) {
  return post(`auth/forgot-password`, { body: { email } });
}

export async function resetPassword({ password, token }) {
  return post(`auth/reset-password`, { body: { token, password } });
}

export async function reEnterPassword({ password }) {
  const response = await post(`auth/reenter-password`, { body: { password } });
  if (response.status < 400) setReEnterPasswordToken(response.data.token);
  return response;
}

export async function resetPinRequest(body) {
  return post('auth/reset-pin-request', { body: body });
}

export async function resetPin({ pin, token }) {
  return post(`auth/reset-pin`, { body: { token, pin } });
}

export async function disablePinRequest(body) {
  return post('auth/disable-pin-request', { body: body });
}

export async function disablePin({ token }) {
  return post(`auth/disable-pin`, { body: { token } });
}

export async function getSignupUserDetail(userId) {
  return get(`auth/signup/detail/${userId}`);
}

export async function updateSignupUserDetail(body) {
  const response = await put(`auth/signup/update`, {
    body: {
      ...body,
      userId: body.userId,
    },
  });
  const { userId } = body;
  if (userId) {
    if (response.status < 400) {
      const { userInfo } = response.data;

      isUserWithout2FA(userInfo)
        ? setAuthorization(response.data.token.token)
        : setAuthorization2FA(response.data.token.token);
    }
  }
  return response;
}

export async function checkVerificationStatus(userId) {
  return get(`auth/signup/status/${userId}`);
}

export async function createFvsSession(userId) {
  return post(`idkit/initiate-facial-verification/${userId}`, {
    body: {
      livenessOnly: true,
    },
  });
}

export async function getEnterpriseList(searchText = '') {
  console.log('search text', searchText);

  return get(`enterprise/select-list/${searchText}`);
}
